import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSnackbar } from "notistack";
import LoadingAnimation from "@assets/svg/LoadingAnimation";
import { getClassificationSummary } from "@utils/classification";
import { getReferralPartnerStats } from "@endpoints/candidate.service";

type ReferralPartner = {
  _id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  profilePic?: string;
  refNo?: string;
  placements?: number;
  totalApplied?: number;
  topPlacedCount?: number;
  topTotalApplications?: number;
  successPercentage?: number;
  successRateBasedOnCandidateStatus?: number;
  topClassification?: {
    main?: string;
    sub?: string[];
  } | null;
  averageResponseTime?: {
    value?: number;
    unit?: "minutes" | "hours";
    label?: string;
    responseCount?: number;
  } | null;
  bestSuccessArea?: {
    jobId?: string;
    jobTitle?: string;
    placements?: number;
    totalApplications?: number;
    successPercentage?: number;
  } | null;
  referralScore?: number | null;
};

type Props = {
  open: boolean;
  onClose: () => void;
  onSelectPartner?: (partner: ReferralPartner) => void;
};

const emptyState = "No referral partners found.";

const sortByBestOverall = (items: ReferralPartner[]) =>
  [...items].sort((a, b) => {
    const successDiff =
      (b.successRateBasedOnCandidateStatus ?? b.successPercentage ?? 0) -
      (a.successRateBasedOnCandidateStatus ?? a.successPercentage ?? 0);
    const placementDiff =
      (b.placements ?? b.topPlacedCount ?? 0) -
      (a.placements ?? a.topPlacedCount ?? 0);
    const bestAreaPlacementDiff =
      (b.bestSuccessArea?.placements ?? 0) -
      (a.bestSuccessArea?.placements ?? 0);
    const bestAreaTotalDiff =
      (b.bestSuccessArea?.totalApplications ?? 0) -
      (a.bestSuccessArea?.totalApplications ?? 0);

    return (
      successDiff ||
      placementDiff ||
      bestAreaPlacementDiff ||
      bestAreaTotalDiff ||
      (a?.refNo || "").localeCompare(b?.refNo || "") ||
      (a?.firstName || "").localeCompare(b?.firstName || "") ||
      (a?.lastName || "").localeCompare(b?.lastName || "") ||
      (a?._id || "").localeCompare(b?._id || "")
    );
  });

const getSuccessPercentage = (partner: ReferralPartner) =>
  Number(
    (
      partner?.successRateBasedOnCandidateStatus ??
      partner?.successPercentage ??
      0
    ).toFixed(2)
  );

const getClassificationLabel = (partner: ReferralPartner) => {
  const placements = Number(
    partner?.placements ?? partner?.topPlacedCount ?? 0
  );

  if (placements <= 0) {
    return "-";
  }

  return getClassificationSummary(partner?.topClassification || undefined);
};

const getAverageResponseMinutes = (partner: ReferralPartner) => {
  const responseTime = partner?.averageResponseTime;
  if (!responseTime?.value || responseTime.value <= 0) {
    return Number.MAX_SAFE_INTEGER;
  }

  return responseTime.unit === "hours"
    ? responseTime.value * 60
    : responseTime.value;
};

const hasMeaningfulIndustryData = (partner: ReferralPartner) => {
  const placements = Number(
    partner?.placements ?? partner?.topPlacedCount ?? 0
  );

  return placements > 0 && getClassificationLabel(partner) !== "-";
};

const mergeReferralPartners = (
  current: ReferralPartner[],
  incoming: ReferralPartner[]
) => {
  const merged = [...current];
  const seen = new Set(current.map((partner) => partner._id));

  incoming.forEach((partner) => {
    if (!partner?._id || seen.has(partner._id)) {
      return;
    }

    seen.add(partner._id);
    merged.push(partner);
  });

  return merged;
};

const ReferralPartnerPopup = ({ open, onClose, onSelectPartner }: Props) => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [items, setItems] = useState<ReferralPartner[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [activeTab, setActiveTab] = useState<
    "bestOverall" | "fastest" | "bestIndustry"
  >("bestOverall");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const fetchReferralPartners = useCallback(
    async (nextPage = 1, replace = true) => {
      try {
        if (replace) {
          setLoading(true);
        } else {
          setLoadingMore(true);
        }

        const response = await getReferralPartnerStats({
          page: nextPage,
          limit: 20,
        });

        const referralPartners =
          response?.data?.referralPartners ??
          response?.referralPartners ??
          response?.data ??
          [];

        const nextItems = Array.isArray(referralPartners)
          ? referralPartners
          : [];

        const nextHasMore = Boolean(
          response?.data?.hasMore ?? response?.hasMore ?? false
        );

        setItems((prev) =>
          replace ? nextItems : mergeReferralPartners(prev, nextItems)
        );
        setPage(nextPage);
        setHasMore(nextHasMore);
      } catch (error: any) {
        enqueueSnackbar(error?.message || "Failed to load referral partners", {
          variant: "error",
        });
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [enqueueSnackbar]
  );

  useEffect(() => {
    if (!open) return;
    setItems([]);
    setPage(1);
    setHasMore(true);
    fetchReferralPartners(1, true);
  }, [fetchReferralPartners, open]);

  const handleScroll = () => {
    const container = scrollRef.current;
    if (!container || loading || loadingMore || !hasMore) return;

    const nearBottom =
      container.scrollTop + container.clientHeight >=
      container.scrollHeight - 120;

    if (nearBottom) {
      fetchReferralPartners(page + 1, false);
    }
  };

  const visibleItems = useMemo(() => {
    if (!items.length) return [];

    return sortByBestOverall(items);
  }, [items]);

  useEffect(() => {
    const container = scrollRef.current;
    if (container) {
      container.scrollTop = 0;
    }
  }, [activeTab]);

  const handleSelectPartner = async (partner: ReferralPartner) => {
    if (!partner?._id) {
      enqueueSnackbar("Referral partner selection is missing.", {
        variant: "error",
      });
      return;
    }

    try {
      setSelectedId(partner._id);
      onSelectPartner?.(partner);
    } catch (error: any) {
      enqueueSnackbar(error?.message || "Unable to select referral partner", {
        variant: "error",
      });
    } finally {
      setSelectedId(null);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 px-4">
      <div className="w-full max-w-5xl rounded-[28px] bg-white p-6 shadow-2xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-[26px] font-semibold text-primary">
              Choose a Referral Partner
            </h2>
            <p className="mt-1 text-xs text-light-black">
              Pick a partner to send your first referral request.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-2xl leading-none text-primary"
            aria-label="Close referral partner popup"
          >
            ×
          </button>
        </div>

        <div className="mt-6 flex gap-3">
          {[
            { key: "bestOverall", label: "Best Overall" },
            { key: "fastest", label: "Fastest" },
            { key: "bestIndustry", label: "Best in My Industry" },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={
                tab.key === "bestOverall"
                  ? () => setActiveTab(tab.key as any)
                  : undefined
              }
              disabled={tab.key !== "bestOverall"}
              className={`rounded-full px-4 py-2 text-xs font-medium transition ${
                activeTab === tab.key
                  ? "bg-[#EAF2FF] text-[#1D4ED8]"
                  : tab.key === "bestOverall"
                  ? "bg-[#F4F6F8] text-primary"
                  : "cursor-not-allowed bg-[#F4F6F8] text-[#A1A7B3] opacity-70"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="mt-6 max-h-[60vh] space-y-4 overflow-y-auto pr-1"
        >
          {loading ? (
            <div className="flex min-h-[240px] items-center justify-center">
              <LoadingAnimation color={"black"} />
            </div>
          ) : visibleItems.length > 0 ? (
            visibleItems.map((partner, index) => {
              const placements = Number(
                partner?.placements ?? partner?.topPlacedCount ?? 0
              );
              const totalApplications = Number(
                partner?.totalApplied ?? partner?.topTotalApplications ?? 0
              );
              const successRate = getSuccessPercentage(partner);
              const badge =
                placements > 0 || successRate > 0
                  ? activeTab === "fastest"
                  ? index === 0
                    ? "Fast Responder"
                    : "Quick Reply"
                  : activeTab === "bestIndustry"
                  ? index === 0
                    ? "Top Industry Fit"
                    : "Strong Match"
                  : index === 0
                  ? "Top Performer"
                  : "High Success"
                  : null;

              const responseLabel = partner.averageResponseTime?.label || "N/A";
              const bestSuccessAreaLabel =
                placements > 0
                  ? getClassificationLabel(partner) ||
                    partner.bestSuccessArea?.jobTitle ||
                    "N/A"
                  : "-";

              return (
                <div
                  key={partner._id}
                  className="grid grid-cols-[1.4fr_0.8fr_0.8fr_0.8fr_0.8fr_0.8fr_auto] items-center gap-4 rounded-[20px] border border-[#D7DFEA] bg-white px-5 py-4 shadow-[0_1px_6px_rgba(17,24,39,0.08)] max-lg:grid-cols-1"
                >
                  <div className="flex items-center gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#EDF2FF] text-sm font-semibold text-[#1D4ED8]">
                      {partner?.profilePic ? (
                        <img
                          src={partner.profilePic}
                          alt={partner.firstName || "Referral partner"}
                          className="h-full w-full rounded-full object-cover"
                        />
                      ) : (
                        <span>
                          {(partner?.firstName || "R").charAt(0)}
                        </span>
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-primary">
                        {partner?.firstName} {partner?.lastName}
                      </p>
                      <p className="mt-1 text-xs text-light-black">
                        Best success area
                      </p>
                      <p className="truncate text-sm font-semibold text-primary">
                        {bestSuccessAreaLabel}
                      </p>
                      {(partner?.placements ?? partner?.topPlacedCount ?? 0) >
                      0 ? (
                        <p className="mt-1 text-xs text-[#8090A7]">
                          {partner?.placements ?? partner?.topPlacedCount ?? 0}{" "}
                          placements in {bestSuccessAreaLabel}
                        </p>
                      ) : null}
                    </div>
                  </div>

                  <div className="text-center">
                    <p className="text-sm font-semibold text-primary">
                      {partner?.placements ?? 0}
                    </p>
                    <p className="mt-1 text-xs text-light-black">Placements</p>
                  </div>

                  <div className="text-center">
                    <p className="text-sm font-semibold text-primary">
                      {getSuccessPercentage(partner)}
                      %
                    </p>
                    <p className="mt-1 text-xs text-light-black">Success</p>
                  </div>

                  <div className="text-center">
                    <p className="text-sm font-semibold text-primary">
                      {responseLabel}
                    </p>
                    <p className="mt-1 text-xs text-light-black">Response</p>
                  </div>

                  <div className="text-center">
                    <p className="text-sm font-semibold text-primary">
                      {partner?.referralScore ?? "—"}
                    </p>
                    <p className="mt-1 text-xs text-light-black">Score</p>
                  </div>

                  <div className="flex justify-center">
                    {badge ? (
                      <span className="rounded-full bg-[#E6F8F0] px-3 py-1 text-xs font-medium text-[#0F9D58]">
                        {badge}
                      </span>
                    ) : null}
                  </div>

                  <button
                    onClick={() => handleSelectPartner(partner)}
                    disabled={selectedId === partner._id}
                    className="rounded-full bg-[#2257FF] px-5 py-2.5 text-sm font-medium text-white transition hover:bg-[#1B46CB] disabled:opacity-60"
                  >
                    {selectedId === partner._id ? "Saving..." : "Select"}
                  </button>
                </div>
              );
              })
            ) : (
              <div className="flex min-h-[240px] items-center justify-center text-sm text-light-black">
                {emptyState}
              </div>
            )}
          {loadingMore ? (
            <div className="flex items-center justify-center py-4">
              <LoadingAnimation color={"black"} />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default ReferralPartnerPopup;
